/**
 * The two-pane view's rail (Option C, C1, plan §3.2 / §3.6, prototype `look/form.png`): All engines, then the engines
 * by group. The chosen engine carries `aria-current`; a dot marks every engine whose draft has input. The rail is ONE
 * tab stop (roving tabindex over All engines and the items); Up / Down move between them, Enter / Space activate.
 * No rail filter (D13): the start view filters. The entry points are a separate element (`RailEntryPoints`) so the Tab
 * order is rail → form → footer → entry points while the grid shows them at the rail's bottom.
 *
 * C1b: each item and caption is a flip item with the same key as its start-view card (`transition/flipPlan.ts`). An
 * item's surface is its own layer, drawn on every item but shown only on the current one, so the cards' boxes can
 * shrink into it and the highlight can slide between items; the item carries a hidden copy of its card's blurb (a
 * `data-flip-ghost`, shown only during a flight) so the blurb can fade out as the card becomes the item.
 */
import { useRef, type KeyboardEvent } from 'react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UserFacingEngineId } from '@/core/engines';
import { ConnectionEngineIcon } from '../../components/ConnectionEngineIcon';
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import { engineCatalogGroup } from '../state/engineCatalog';
import { engineOptionId } from '../start/engineFilter';

type Props = {
  engine: DatabaseEngine;
  enginesWithInput: ReadonlySet<DatabaseEngine>;
  onPick: (engine: UserFacingEngineId) => void;
  onAllEngines: () => void;
  className?: string;
};

const GROUPS = [
  { id: 'databases', caption: 'Databases' },
  { id: 'azure', caption: 'Azure services' },
] as const;

const ITEM = 'relative flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-[13.5px] outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function EngineRail({ engine, enginesWithInput, onPick, onAllEngines, className }: Props) {
  const listRef = useRef<HTMLElement>(null);

  // Up / Down move focus between All engines and the items (buttons: Enter and Space activate natively).
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const stops = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[data-rail-stop]') ?? []);
    const index = stops.indexOf(document.activeElement as HTMLElement);
    if (index < 0) return;
    event.preventDefault();
    stops[Math.max(0, Math.min(stops.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))]?.focus();
  };

  return (
    <nav ref={listRef} aria-label="Engines" data-testid="engine-tile-selector" onKeyDown={onKeyDown} className={cn('flex flex-col px-2 pt-2.5 pb-2', className)}>
      <button
        type="button"
        data-rail-stop
        data-focus-role="all-engines"
        tabIndex={-1}
        onClick={onAllEngines}
        data-flip-chrome="fade"
        className={cn(ITEM, 'mb-0.5 w-auto self-start text-[13px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground')}
        data-testid="rail-all-engines-button"
      >
        <ArrowLeft className="h-[15px] w-[15px]" aria-hidden />
        All engines
      </button>
      {GROUPS.map((group) => (
        <div key={group.id} role="group" aria-labelledby={`connection-rail-group-${group.id}`}>
          <div id={`connection-rail-group-${group.id}`} className="px-2 pb-1 pt-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {/* inline-block: a transform does not apply to an inline box. */}
            <span data-flip-key={`caption:${group.id}`} data-flip-layer="label" className="relative inline-block">
              {group.caption}
            </span>
          </div>
          {engineCatalogGroup(group.id).map((entry) => {
            const current = entry.id === engine;
            return (
              <button
                key={entry.id}
                type="button"
                data-rail-stop
                data-focus-role="engine"
                tabIndex={current ? 0 : -1}
                aria-current={current ? 'true' : undefined}
                onClick={() => onPick(entry.id)}
                className={cn(ITEM, current ? 'font-medium' : 'hover:bg-muted/75')}
                data-testid={`engine-tile-${entry.id}`}
                data-flip-key={engineOptionId(entry.id)}
              >
                <span aria-hidden data-flip-layer="surface" className={cn('absolute inset-0 rounded-md bg-background shadow-xs ring-1 ring-border', !current && 'opacity-0')}>
                  {current && <span data-flip-layer="bar" className="absolute left-0.5 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-sm bg-primary" />}
                </span>
                <span data-flip-layer="icon" className="relative flex shrink-0">
                  <ConnectionEngineIcon engine={entry.id} iconOnly size="sm" className="h-[18px] w-[18px]" />
                </span>
                <span data-flip-layer="label" className="relative min-w-0 flex-1 truncate">
                  {entry.label}
                </span>
                <span aria-hidden data-flip-layer="blurb" data-flip-ghost className="absolute left-9 top-full whitespace-normal text-left text-xs font-normal leading-snug text-muted-foreground opacity-0">
                  {entry.blurb}
                </span>
                {enginesWithInput.has(entry.id) && (
                  <span data-flip-layer="dot" className="relative h-1.5 w-1.5 shrink-0 rounded-full bg-primary" data-testid={`engine-draft-dot-${entry.id}`} aria-label="Has a draft" role="img" />
                )}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
